import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { PACKAGES, DESTINATIONS, ITINERARY_SAMPLE, COMPANY } from '../mock';
import { PackageCard } from '../components/sections/Packages';
import { Button } from '../components/ui/button';
import { Star, Clock, Users, MapPin, CheckCircle2, XCircle, MessageCircle, Phone, Tag, Play } from 'lucide-react';
import EnquiryForm from '../components/sections/EnquiryForm';
import VideoGallery from '../components/sections/VideoGallery';
import ReviewCards from '../components/sections/ReviewCards';
import CustomerVideoReviews from '../components/sections/CustomerVideoReviews';
import { getVideoReviewsForPackage, VIDEO_REVIEWS, resolveReviewKey } from '../constants/videoReviews';

export function PackagesPage() {
  const [filter, setFilter] = useState('all');
  const filtered = useMemo(() => filter === 'all' ? PACKAGES : PACKAGES.filter(p => p.destination === filter), [filter]);

  return (
    <div>
      <Helmet>
        <title>Today's Offers - Flat 25% OFF Packages | Mountains Chain Travel</title>
        <meta name="description" content="Flat 25% OFF Packages. Limited Time Travel Deals. Hand-crafted tour packages for Himachal, Kashmir, Ladakh, and Uttarakhand." />
        <meta property="og:title" content="Today's Offers - Flat 25% OFF Packages | Mountains Chain Travel" />
        <meta property="og:description" content="Flat 25% OFF Packages. Limited Time Travel Deals. Hand-crafted tour packages for Himachal, Kashmir, Ladakh, and Uttarakhand." />
      </Helmet>

      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <img src={PACKAGES[0].image} alt="Tour Packages" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-blue/85 via-brand-blue/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/70 via-transparent to-brand-blue/30" />
        <div className="relative z-20 max-w-7xl mx-auto px-6 py-20 w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2.5 px-5 py-2 bg-brand-orange rounded-full text-white text-[13px] font-bold mb-6 shadow-lg shadow-brand-orange/40">
              <span className="tracking-wide">Customizable Packages · Best Prices Guaranteed</span>
            </div>
            <h1 className="font-poppins text-[48px] sm:text-[56px] lg:text-[72px] text-white leading-[1.05] font-extrabold drop-shadow-2xl">
              <span className="text-brand-orange">Tour</span>
              <br />
              <span>Packages</span>
            </h1>
            <p className="text-white text-[18px] lg:text-[21px] mt-5 max-w-xl leading-relaxed font-medium drop-shadow-lg">
              Hand-crafted itineraries for Himachal, Kashmir, Ladakh & Uttarakhand. Customize any package to your dates, group and budget.
            </p>
            <div className="mt-6 inline-flex flex-wrap items-center gap-2 px-4 py-2.5 bg-white/10 backdrop-blur-md rounded-xl border border-white/20">
              <span className="bg-brand-orange text-white text-[12px] font-bold px-2.5 py-1 rounded-md uppercase tracking-wider">Destinations</span>
              <span className="text-white text-[14px] lg:text-[15px] font-semibold">
                Himachal · Kashmir · Ladakh · Uttarakhand · North East
              </span>
            </div>
            <div className="flex flex-wrap gap-8 mt-10">
              {[
                { num: '100+', label: 'Packages' },
                { num: '4.9', label: '★ Rating' },
                { num: '10000+', label: 'Travelers Served' },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-brand-orange font-poppins font-extrabold text-[28px] lg:text-[32px] leading-none">{stat.num}</div>
                  <div className="text-white/80 text-[12px] font-medium mt-1 uppercase tracking-wider">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Review Cards - Right after hero */}
      <ReviewCards />
      <CustomerVideoReviews />

      <section id="live-offers" className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-wrap gap-2 mb-8">
          <FilterPill active={filter === 'all'} onClick={() => setFilter('all')}>All ({PACKAGES.length})</FilterPill>
          {DESTINATIONS.map(d => (
            <FilterPill key={d.slug} active={filter === d.slug} onClick={() => setFilter(d.slug)}>
              {d.name} ({PACKAGES.filter(p => p.destination === d.slug).length})
            </FilterPill>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map(p => <PackageCard key={p.id} pkg={p} />)}
        </div>
      </section>

      <EnquiryForm />
    </div>
  );
}

function FilterPill({ active, onClick, children }) {
  return (
    <button onClick={onClick} className={`px-4 py-2 rounded-full text-[13px] font-semibold transition-colors ${active ? 'bg-brand-blue text-white' : 'bg-brand-grey text-brand-blue hover:bg-brand-greyDark'}`}>
      {children}
    </button>
  );
}

export function PackageDetailPage() {
  const { slug } = useParams();
  const pkg = PACKAGES.find(p => p.slug === slug);
  const [tab, setTab] = useState('itinerary');
  if (!pkg) return <div className="max-w-7xl mx-auto px-6 py-20 text-center">Package not found. <Link to="/packages" className="text-brand-orange">Back</Link></div>;
  const dest = DESTINATIONS.find(d => d.slug === pkg.destination);
  const discount = Math.round(((pkg.oldPrice - pkg.price) / pkg.oldPrice) * 100);
  const similar = PACKAGES.filter(p => p.destination === pkg.destination && p.id !== pkg.id).slice(0, 3);
  const isTaxi = pkg.tags.includes('Taxi Tour');

  return (
    <div>
      <Helmet>
        <title>{pkg.title} | Mountains Chain Travel</title>
        <meta name="description" content={`Book the ${pkg.title} package for ₹${pkg.price}. ${pkg.nights} Nights / ${pkg.days} Days perfectly curated itinerary by Mountains Chain Travel.`} />
        <meta property="og:title" content={`${pkg.title} | Mountains Chain Travel`} />
        <meta property="og:description" content={`Book the ${pkg.title} package for ₹${pkg.price}. ${pkg.nights} Nights / ${pkg.days} Days perfectly curated itinerary by Mountains Chain Travel.`} />
      </Helmet>

      <section className="relative min-h-[65vh] flex items-center overflow-hidden">
        <img src={pkg.image} alt={pkg.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-blue/85 via-brand-blue/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/70 via-transparent to-brand-blue/30" />
        <div className="relative z-20 max-w-7xl mx-auto px-6 py-16 w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-brand-orange/10 rounded-full mb-4">
              <span className="text-brand-orange text-[12px] font-bold uppercase tracking-wider">{dest?.name}</span>
            </div>
            <h1 className="font-poppins text-[36px] sm:text-[44px] lg:text-[56px] text-white leading-[1.1] font-extrabold drop-shadow-2xl">
              {pkg.title}
            </h1>
            <p className="text-white text-[16px] lg:text-[18px] mt-4 max-w-xl leading-relaxed font-medium">
              {pkg.nights} Nights / {pkg.days} Days · {dest?.name}
            </p>
            <div className="flex flex-wrap gap-4 mt-6">
              <div className="flex items-center gap-2 text-white">
                <Star size={16} className="fill-brand-orange text-brand-orange"/>
                <span className="font-semibold">{pkg.rating}</span>
                <span className="text-white/70">({pkg.reviews} reviews)</span>
              </div>
              {discount > 0 && (
                <span className="px-3 py-1 bg-brand-orange text-white text-[12px] font-bold rounded-md">{discount}% OFF</span>
              )}
            </div>
            <div className="flex flex-wrap gap-3 mt-6">
              <a href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent('Hi Mountains Chain Travel! 👋\n\nI\'m interested in the "' + pkg.title + '" package (' + pkg.nights + 'N/' + pkg.days + 'D) for ₹' + pkg.price.toLocaleString('en-IN') + '/person.\n\nCould you please share the detailed itinerary, hotel options, and any current offers?\n\nThank you!')}`} target="_blank" rel="noreferrer">
                <Button className="bg-brand-orange hover:bg-brand-orangeDark text-white h-12 px-7 rounded-xl text-[15px] font-bold btn-glow shadow-xl shadow-brand-orange/30">
                  <MessageCircle size={18} className="mr-2"/>WhatsApp for Best Quote
                </Button>
              </a>
              <a href={`tel:${COMPANY.phone}`}>
                <Button variant="outline" className="border-2 border-white/50 text-white hover:bg-white/20 h-12 px-7 rounded-xl text-[15px] font-semibold">
                  <Phone size={18} className="mr-2"/>Call to Book
                </Button>
              </a>
            </div>
            <div className="flex flex-wrap items-center gap-6 mt-8">
              {[
                { num: pkg.nights + 'N / ' + pkg.days + 'D', label: 'Duration' },
                { num: '₹' + pkg.price.toLocaleString('en-IN'), label: isTaxi ? 'Starting From' : 'Per Person' },
              ].map((stat, i) => (
                <div key={i}>
                  <div className="text-brand-orange font-poppins font-extrabold text-[24px] leading-none">{stat.num}</div>
                  <div className="text-white/70 text-[11px] font-medium mt-1 uppercase tracking-wider">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <ReviewCards destinationKey={resolveReviewKey(slug) || resolveReviewKey(pkg.destination) || pkg.destination} />
      <CustomerVideoReviews reviews={getVideoReviewsForPackage(slug, pkg.destination)} />

      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="flex flex-wrap gap-3 items-center mb-4">
              <span className="flex items-center gap-1 text-brand-blue text-[14px] font-semibold"><Star size={15} className="fill-brand-orange text-brand-orange"/>{pkg.rating} ({pkg.reviews} reviews)</span>
              <span className="flex items-center gap-1 text-brand-blue/70 text-[14px]"><Clock size={14}/>{pkg.nights}N / {pkg.days}D</span>
              <span className="flex items-center gap-1 text-brand-blue/70 text-[14px]"><MapPin size={14}/>{dest?.name}</span>
              {pkg.tags.map(t => <span key={t} className="px-2.5 py-1 bg-brand-orange/10 text-brand-orange text-[11px] font-bold uppercase tracking-wider rounded-md">{t}</span>)}
            </div>

            <div className="flex gap-1 border-b border-brand-greyDark mt-6">
              {['itinerary','inclusions','reviews'].map(t => (
                <button key={t} onClick={() => setTab(t)} className={`px-4 py-3 text-[14px] font-semibold capitalize border-b-2 transition-colors ${tab === t ? 'border-brand-orange text-brand-orange' : 'border-transparent text-brand-blue/60 hover:text-brand-blue'}`}>{t}</button>
              ))}
            </div>

            <div className="py-6">
              {tab === 'itinerary' && (
                <div className="space-y-5">
                  {ITINERARY_SAMPLE.slice(0, pkg.days).map(d => (
                    <div key={d.day} className="flex gap-4">
                      <div className="w-12 h-12 rounded-xl bg-brand-blue text-brand-orange font-poppins font-bold flex items-center justify-center shrink-0">D{d.day}</div>
                      <div>
                        <h4 className="font-poppins font-semibold text-brand-blue text-[16px]">{d.title}</h4>
                        <p className="text-brand-blue/70 text-[14px] mt-1 leading-relaxed">{d.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {tab === 'inclusions' && (
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-poppins font-semibold text-brand-blue mb-3">What's included</h4>
                    <ul className="space-y-2">
                      {[...pkg.inclusions, 'Airport / station transfers', 'Toll & parking', 'GST & service charges'].map((x,i) => (
                        <li key={i} className="flex items-start gap-2 text-brand-blue/80 text-[14px]"><CheckCircle2 size={17} className="text-emerald-600 mt-0.5 shrink-0"/>{x}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-poppins font-semibold text-brand-blue mb-3">Not included</h4>
                    <ul className="space-y-2">
                      {['Personal expenses','Optional adventure activities','Travel insurance','Anything not listed above'].map((x,i) => (
                        <li key={i} className="flex items-start gap-2 text-brand-blue/65 text-[14px]"><XCircle size={17} className="text-brand-blue/30 mt-0.5 shrink-0"/>{x}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              {tab === 'reviews' && (
                <div className="space-y-4">
                  {[
                    { name: 'Sayani R', city: 'Dharamshala & Dalhousie', img: 'https://i.pravatar.cc/80?img=25', text: 'We had a wonderful family trip to Dharamshala & Dalhousie. Everything - from the amazing hotels to sightseeing and transport - was smooth and hassle-free. Miss Simran Thakur managed our entire tour and was very helpful, punctual, and professional. Any small issue was resolved immediately. Highly recommend Mountains Chain Travel for a stress-free Himalayan holiday.' },
                    { name: 'Kunal C', city: 'Spiti Valley', img: 'https://i.pravatar.cc/80?img=32', text: 'A Truly Unforgettable Spiti Valley Journey! The itinerary was perfectly planned - covered Shimla, Sangla, Chitkul, Kalpa, Tabo, Kaza, Chandratal, and Manali without feeling rushed. Comfortable stays, experienced driver, and excellent coordination by Simran ji with regular check-ins. Clear, transparent process with no hidden surprises.' },
                    { name: 'Dipten Mondal', city: 'Himachal Pradesh', img: 'https://i.pravatar.cc/80?img=12', text: 'We visited Himachal for 7 days and this travel agency was excellent. Special mention to our driver Manjeet Singh - very safe, punctual, and friendly. He also guided us very well throughout the trip. Good itinerary and the advisor stays in touch throughout, making travellers feel comfortable.' },
                  ].map((r, i) => (
                    <div key={i} className="bg-brand-grey rounded-xl p-5">
                      <div className="flex items-center gap-3">
                        <img src={r.img} alt={r.name} className="w-10 h-10 rounded-full" />
                        <div>
                          <div className="font-semibold text-brand-blue text-[14px]">{r.name}</div>
                          <div className="text-[11px] text-brand-blue/50">{r.city}</div>
                          <div className="flex mt-0.5">{[1,2,3,4,5].map(s => <Star key={s} size={12} className="fill-brand-orange text-brand-orange"/>)}</div>
                        </div>
                      </div>
                      <p className="text-brand-blue/75 text-[14px] mt-3 leading-relaxed">{r.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <aside className="bg-white border border-brand-greyDark rounded-2xl p-6 h-fit lg:sticky lg:top-24 shadow-card">
            {discount > 0 && <div className="inline-flex items-center gap-1 bg-brand-orange/10 text-brand-orange text-[11px] font-bold px-2 py-1 rounded-md mb-3"><Tag size={11}/>{discount}% OFF</div>}
            <div className="text-[12px] text-brand-blue/55 line-through">₹{pkg.oldPrice.toLocaleString('en-IN')}</div>
            <div className="font-poppins font-bold text-brand-blue text-[34px] leading-none">{isTaxi ? 'From ' : ''}₹{pkg.price.toLocaleString('en-IN')}<span className="text-[13px] text-brand-blue/55 font-normal">{isTaxi ? '' : '/person'}</span></div>
            <div className="text-[12px] text-brand-blue/55 mt-1">All taxes & transfers included</div>
            <div className="flex flex-col gap-2.5 mt-5">
              <a href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent('Hi Mountains Chain Travel! 👋\n\nI\'m interested in the "' + pkg.title + '" package.\n\nPlease share:\n- Detailed day-wise itinerary\n- Hotel options & meal plans\n- Any ongoing discounts\n\nThanks!')}`} target="_blank" rel="noreferrer">
                <Button className="w-full bg-brand-orange hover:bg-brand-orangeDark text-white h-11 rounded-xl btn-glow"><MessageCircle size={16} className="mr-2"/>WhatsApp for Best Quote</Button>
              </a>
              <a href={`tel:${COMPANY.phone}`}>
                <Button variant="outline" className="w-full border-brand-blue/20 text-brand-blue hover:bg-brand-blue hover:text-white h-11 rounded-xl"><Phone size={16} className="mr-2"/>Call to Book</Button>
              </a>
            </div>
            <div className="mt-5 pt-5 border-t border-brand-grey text-[12.5px] text-brand-blue/70 space-y-2">
              <div className="flex items-start gap-2"><Users size={14} className="text-brand-orange mt-0.5"/>Customizable for groups of 2 to 30+</div>
              <div className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-orange mt-0.5"/>Free cancellation up to 14 days</div>
              <div className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-orange mt-0.5"/>Pay just 20% to book</div>
            </div>
          </aside>
        </div>

        {(pkg.destination === 'leh-ladakh' || pkg.tags.includes('Leh Ladakh')) && (
          <div className="mt-16">
            <div className="flex items-center gap-2 mb-6">
              <Play size={18} className="text-brand-orange" fill="currentColor" />
              <h2 className="font-poppins text-[26px] text-brand-blue">Ladakh in Motion</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {VIDEO_REVIEWS['leh-ladakh'].slice(0, 3).map((v) => (
                <div key={v.id} className="relative rounded-2xl overflow-hidden group aspect-video bg-brand-blue shadow-card">
                  <iframe
                    src={`https://www.youtube.com/embed/${v.id}?rel=0&modestbranding=1`}
                    title={v.name}
                    className="absolute inset-0 w-full h-full border-0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent pointer-events-none">
                    <div className="text-white font-poppins font-bold text-[15px]">{v.destination}</div>
                    <div className="text-white/70 text-[12px]">{v.name}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {similar.length > 0 && (
          <div className="mt-16">
            <h2 className="font-poppins text-[26px] text-brand-blue mb-6">You may also like</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {similar.map(s => <PackageCard key={s.id} pkg={s} />)}
            </div>
          </div>
        )}
      </section>
      <EnquiryForm />
    </div>
  );
}
